import { useState } from 'react';
import type { AlphaUpscaleMode, UpscaleProgress } from '@design-ai/pipeline';
import type { Engine } from '../engine.js';
import { ProgressBar, Section, Select, Slider, Toggle } from './controls.js';

export interface UpscalePanelProps {
  engine: Engine;
  ready: boolean;
  onDone: () => void;
}

export function UpscalePanel({ engine, ready, onDone }: UpscalePanelProps) {
  const [, forceRender] = useState(0);
  const [progress, setProgress] = useState<UpscaleProgress | null>(null);
  const [error, setError] = useState<string | null>(null);
  const settings = engine.workbench.upscaleSettings;

  const update = <K extends keyof typeof settings>(key: K, value: (typeof settings)[K]): void => {
    settings[key] = value;
    forceRender((n) => n + 1);
  };

  const run = async (): Promise<void> => {
    setError(null);
    setProgress({ tile: 0, totalTiles: 1, stage: 'color' });
    try {
      await engine.workbench.runUpscale(setProgress);
      onDone();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setProgress(null);
    }
  };

  const state = engine.workbench.state;

  return (
    <Section title="Upscale" subtitle="Tiled super-resolution over the cut-out.">
      <Slider
        label="Tile size"
        value={settings.tileSize}
        min={128}
        max={512}
        step={32}
        onChange={(value) => update('tileSize', value)}
        format={(value) => `${value}px`}
        hint="Smaller tiles use less VRAM; larger tiles are faster per pixel."
      />
      <Slider
        label="Tile context"
        value={settings.overlap}
        min={0}
        max={48}
        step={4}
        onChange={(value) => update('overlap', value)}
        format={(value) => `${value}px`}
        hint="Padding fed to the network and then discarded. Too little shows tile seams."
      />
      <Select<AlphaUpscaleMode>
        label="Alpha channel"
        value={settings.alphaMode}
        options={[
          { value: 'guided', label: 'Guided (recommended)' },
          { value: 'model', label: 'Run the model on alpha (2x slower)' },
          { value: 'lanczos', label: 'Lanczos only (fastest)' },
        ]}
        onChange={(value) => update('alphaMode', value)}
        hint="SR models are RGB-only, so the matte is scaled separately."
      />
      <Toggle
        label="Fill colour under transparency"
        checked={settings.inpaintTransparent}
        onChange={(value) => update('inpaintTransparent', value)}
        hint="Stops the network sharpening leftover garment texture into the artwork's edge."
      />
      <Slider
        label="Sharpen"
        value={settings.sharpenAmount}
        min={0}
        max={1.5}
        step={0.05}
        onChange={(value) => update('sharpenAmount', value)}
        format={(value) => (value === 0 ? 'off' : value.toFixed(2))}
      />

      {progress ? (
        <ProgressBar
          value={progress.totalTiles ? progress.tile / progress.totalTiles : null}
          label={`${progress.stage}: tile ${progress.tile}/${progress.totalTiles}`}
        />
      ) : (
        <button
          type="button"
          className="button button--primary"
          disabled={!ready || !state.hasMatte}
          onClick={() => void run()}
        >
          Upscale
        </button>
      )}
      {error ? <p className="error">{error}</p> : null}
      {state.hasUpscale ? (
        <p className="ok">
          Output {state.outputWidth} x {state.outputHeight}
        </p>
      ) : null}
    </Section>
  );
}
